import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback } from 'react';
import { UserType } from 'api/models';
import { useUser } from 'hooks/use-user';
import { ProjectResourceOrUndefined, useWithAccess } from './useWithAccess';
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export function withAccess(WrappedComponent) {
    const WithAccess = /*#__PURE__*/ forwardRef((props, ref)=>{
        const { rule, resourceData, propsIfNoAccess, children, actionScope, projectId } = props;
        const { hasAccessToResource } = useWithAccess();
        const hasAccess = hasAccessToResource({
            rule,
            resourceData,
            actionScope,
            projectId
        });
        if (!hasAccess) {
            return propsIfNoAccess ? /*#__PURE__*/ _jsx(WrappedComponent, {
                ref: ref,
                hasAccess: hasAccess,
                actionScope: actionScope,
                ...props,
                ...propsIfNoAccess,
                children: children
            }) : null;
        }
        return /*#__PURE__*/ _jsx(WrappedComponent, {
            ref: ref,
            hasAccess: hasAccess,
            actionScope: actionScope,
            ...props,
            children: children
        });
    });
    const name = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    WithAccess.displayName = `withAccess(${name})`;
    return WithAccess;
}
export const HideIfNotAllowed = ({ children, ...rest })=>{
    var _userData_user;
    const userData = useUser();
    const { hasAccessToResource } = useWithAccess();
    if ((userData === null || userData === void 0 ? void 0 : (_userData_user = userData.user) === null || _userData_user === void 0 ? void 0 : _userData_user.userType) === UserType.CustomerPortalBot && (rest === null || rest === void 0 ? void 0 : rest.ignoreForBotUsers)) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: children
        });
    }
    return hasAccessToResource(rest) ? /*#__PURE__*/ _jsx(_Fragment, {
        children: children
    }) : /*#__PURE__*/ _jsx(_Fragment, {});
};
export const SwitchContentBasedOnAccess = ({ hasAccess, doesNotHaveAccess, ...rest })=>{
    const { hasAccessToResource } = useWithAccess();
    return hasAccessToResource(rest) ? hasAccess : doesNotHaveAccess;
};
export const useCheckRequestScopeForProject = (project)=>{
    var _project_projectOwner, _userData_user;
    const { hasAccessToResource, getRequestScopeForProjectResource } = useWithAccess();
    const userData = useUser();
    const projectResource = ProjectResourceOrUndefined(project);
    const hasRequestScopeAccess = useCallback(({ hasData, rule })=>{
        const hasAccessToTheResource = hasAccessToResource({
            rule,
            resourceData: projectResource
        });
        const requestScope = getRequestScopeForProjectResource({
            rule,
            resourceData: projectResource
        });
        if (hasAccessToTheResource && requestScope === 'RESTRICTED') {
            return hasData;
        }
        if (hasAccessToTheResource && requestScope === 'PROJECT_OWNER') {
            var _project_projectOwner, _userData_user;
            return (project === null || project === void 0 ? void 0 : (_project_projectOwner = project.projectOwner) === null || _project_projectOwner === void 0 ? void 0 : _project_projectOwner.userId) === (userData === null || userData === void 0 ? void 0 : (_userData_user = userData.user) === null || _userData_user === void 0 ? void 0 : _userData_user.userId);
        }
        return hasAccessToTheResource;
    }, [
        getRequestScopeForProjectResource,
        hasAccessToResource,
        projectResource,
        project === null || project === void 0 ? void 0 : (_project_projectOwner = project.projectOwner) === null || _project_projectOwner === void 0 ? void 0 : _project_projectOwner.userId,
        userData === null || userData === void 0 ? void 0 : (_userData_user = userData.user) === null || _userData_user === void 0 ? void 0 : _userData_user.userId
    ]);
    return {
        hasRequestScopeAccess
    };
};
